import { faEdit, faTrash, faUpload } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useFormik } from "formik";
import React, { useEffect, useState } from "react";
import { Button, Col, Form, Modal, Row, Spinner } from "react-bootstrap";
import ReactPaginate from "react-paginate";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Yup from "yup";
import DynamicTable from "../../../components/Tables";
import {
  createAgency,
  deleteAgencyCall,
  getAllAgencies,
  toggleAgencyStatus,
  updateAgency,
  uploadTagsFile,
} from "../../../services/apis";

const Users = ({ className }) => {
  const profile = useSelector((state) => state.profile);
  const [agencyDetails, setAgencyDetails] = useState({
    agency: {},
    users: [],
  });

  const [adminId, setAdminId] = useState(null);
  const [deleteAgency, setDeleteAgency] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isButtonClicked, setIsButtonClicked] = useState(false);
  const [createAgencyModal, setCreateAgencyModal] = useState(false);
  const [updateAgencyModal, setUpdateAgencyModal] = useState(false);
  const [fileUploadModal, setFileUploadModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [usersData, setUsersData] = useState([]);
  const [error, setError] = useState(null);
  const [file, setFile] = useState(null);
  const [selectedAgencyId, setSelectedAgencyId] = useState(null);
  const [agencyIds, setAgencyIds] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [originalData, setOriginalData] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [itemOffset, setItemOffset] = useState(0);
  const itemsPerPage = 10;

  const offset = currentPage * itemsPerPage;
  const currentItems = usersData.slice(offset, offset + itemsPerPage);
  const pageCount = Math.ceil(usersData.length / itemsPerPage);

  const navigate = useNavigate();

  async function agencyActivate(rowData) {
    const previousState = rowData.isactive;
    const updatedStatus = !previousState;
    try {
      const response = await toggleAgencyStatus(rowData.agencyid);
      if (response.statuscode === 1) {
        rowData.isactive = updatedStatus;
        toast.dismiss();
        toast.success(response.message);
        fetchUsers();
      } else {
        rowData.isactive = previousState;
        toast.dismiss();
        toast.error(response.message || "Failed to update category status.");
      }
    } catch (error) {
      rowData.isactive = previousState;
      toast.dismiss();
      toast.error("Failed to update category status. Please try again.");
    }
  }

  const handleSearch = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    const filteredData = originalData.filter(
      (item) =>
        !item.deleted &&
        item.agency?.toLowerCase().includes(query.toLowerCase())
    );

    setUsersData(filteredData);
    setCurrentPage(0);
  };

  const handleSearchBlur = () => {
    if (usersData.length === 0) {
      setSearchQuery("");
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (uploadedFile) {
      setIsUploading(true);
      await uploadFile(uploadedFile);
      setIsUploading(false);
      // setFileUploadModal(false);
    } else {
      toast.dismiss();
      toast.error("Please select a file before submitting.");
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (file) {
      setUploadedFile(file);
    }
  };

  const handleFileRemove = () => {
    setUploadedFile(null);
  };

  const createSystemUserForm = useFormik({
    initialValues: {
      agencyName: "",
      address: "",
      state: "",
      city: "",
      contactEmail: "",
      contactName: "",
      contactPhone: "",
      zipcode: "",
    },
    validationSchema: Yup.object().shape({
      agencyName: Yup.string().required("Agency name cannot be empty."),
      address: Yup.string().required("Address cannot be empty."),
      state: Yup.string().required("State cannot be empty."),
      city: Yup.string().required("City cannot be empty."),
      contactEmail: Yup.string()
        .email("Enter a valid email address.")
        .required("Contact email cannot be empty.")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Enter a valid email address"
        ),
      contactName: Yup.string().required("Contact name cannot be empty."),
      contactPhone: Yup.string().required("Contact phone cannot be empty."),
      zipcode: Yup.string().required("Zipcode cannot be empty."),
    }),
    onSubmit: async (values) => {
      setIsButtonClicked(true);
      const response = await createAgency(values);
      if (response.statuscode === 1) {
        toast.dismiss();
        toast.success(response.message || "Agency created successfully.");
        setItemOffset(0);
        setCurrentPage(0);
        createSystemUserForm.resetForm();
        fetchUsers();
        setCreateAgencyModal(false);
      } else {
        toast.dismiss();
        toast.warn(response.message || "Agency creation failed.");
      }
      setIsButtonClicked(false);
    },
  });

  const updateSystemUserForm = useFormik({
    initialValues: {
      agencyName: "",
      address: "",
      state: "",
      city: "",
      contactEmail: "",
      contactName: "",
      contactPhone: "",
      zipcode: "",
    },
    validationSchema: Yup.object().shape({
      agencyName: Yup.string().required("Agency name cannot be empty."),
      address: Yup.string().required("Address cannot be empty."),
      state: Yup.string().required("State cannot be empty."),
      city: Yup.string().required("City cannot be empty."),
      contactEmail: Yup.string()
        .email("Enter a valid email address.")
        .required("Contact email cannot be empty.")
        .matches(
          /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
          "Enter a valid email address"
        ),
      contactName: Yup.string().required("Contact name cannot be empty."),
      contactPhone: Yup.string().required("Contact phone cannot be empty."),
      zipcode: Yup.string().required("Zipcode cannot be empty."),
    }),
    onSubmit: async (values) => {
      setIsButtonClicked(true);
      try {
        const form = new FormData();
        form.append("agencyid", selectedAgencyId.agencyid);
        form.append("agencyName", values.agencyName);
        form.append("address", values.address);
        form.append("state", values.state);
        form.append("city", values.city);
        form.append("contactEmail", values.contactEmail);
        form.append("contactName", values.contactName);
        form.append("contactPhone", values.contactPhone);
        form.append("zipcode", values.zipcode);

        const response = await updateAgency(form, selectedAgencyId.agencyid);
        if (response) {
          toast.dismiss();
          toast.success("Agency Updated Successfully");
          updateSystemUserForm.resetForm();
          fetchUsers();
          setUpdateAgencyModal(false);
        }
      } catch (error) {
        toast.error("Failed to Update agency.");
      } finally {
        setIsButtonClicked(false);
      }
    },
  });

  const fetchUsers = async () => {
    const response = await getAllAgencies();
    setIsLoading(false);
    if (response && response.statuscode === 1) {
      setOriginalData(response.data);
      setUsersData(response.data);
      setSearchQuery("");
    }
  };

  useEffect(() => {
    setIsLoading(true);
    fetchUsers();
  }, []);

  const handleDeleteConfirm = async () => {
    if (!selectedAgencyId?.agencyid) return;

    setIsLoading(true);

    const result = await deleteAgencyCall(selectedAgencyId?.agencyid);

    if (result?.statuscode === 1) {
      setOriginalData((prevData) =>
        prevData.map((agency) =>
          agency.agencyid === selectedAgencyId?.agencyid
            ? { ...agency, deleted: true }
            : agency
        )
      );

      setUsersData((prevUsers) =>
        prevUsers.filter(
          (agency) => agency.agencyid !== selectedAgencyId?.agencyid
        )
      );

      toast.dismiss();
      toast.success("Agency Deleted Successfully");
      setDeleteAgency(false);
      fetchUsers();
    } else {
      toast.dismiss();
      toast.error("Failed to delete agency. Please try again.");
    }
    setIsLoading(false);
  };

  const uploadFile = async (file) => {
    if (!selectedAgencyId?.agencyid) {
      toast.dismiss();
      toast.error("Please select an agency before uploading the file.");
      return;
    }

    const formData = new FormData();
    formData.append("file", file);
    formData.append("agencyid", selectedAgencyId?.agencyid);

    try {
      const response = await uploadTagsFile(formData);
      console.log(response);
      if (response && response.statuscode === 1) {
        toast.dismiss();
        toast.success(response.message);
        setFileUploadModal(false);
      } else {
        toast.dismiss();
        toast.warning(response.message || "Error In Uploading Tags");
      }
    } catch (error) {
      console.log(error);
      toast.dismiss();
      toast.error(error?.response?.message || "An error occurred");
    }
  };

  const handleCloseAgencyModal = () => {
    setCreateAgencyModal(false);
    createSystemUserForm.resetForm();
  };
  const handleUpdateCloseAgencyModal = () => {
    setUpdateAgencyModal(false);
    updateSystemUserForm.resetForm({
      values: updateSystemUserForm.initialValues,
    });
  };

  useEffect(() => {
    if (!fileUploadModal) {
      setUploadedFile(null);
    }
  }, [fileUploadModal]);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  const cols = (
    navigate,
    agencyActivate,
    setSelectedAgencyId,
    setUpdateAgencyModal,
    setFileUploadModal,
    setDeleteAgency,
    updateSystemUserForm
  ) => {
    return [
      {
        id: "serial",
        title: "S.No",
        isSortable: false,
        render: (_, index) => index + 1,
      },
      {
        id: "agency",
        title: "Agency Name",
        key: "agency",
        isSortable: true,
        iskey: false,
        render: (value) => (
          <span
            className="text-decoration-underline fw-normal hover:fw-bold "
            style={{ cursor: "pointer" }}
            onClick={() => {
              console.log("Clicked value:", value);
              // Navigate with both agencyId and agency name
              // navigate(
              //   `/super-admin/UsersList?agencyId=${
              //     value.agencyid
              //   }&agencyName=${encodeURIComponent(
              //     value.agency
              //   )}&address=${encodeURIComponent(
              //     value.address
              //   )}&agencylogo=${encodeURIComponent(value.agencylogo)}`
              // );
              navigate("/super-admin/UsersList", {
                state: { AgencyData: value },
              });
            }}
          >
            {value.agency || "N/A"}
          </span>
        ),
      },
      {
        id: "primarycontactname",
        title: "Contact Name",
        key: "primarycontactname",
        iskey: true,
        isSortable: true,
        render: (value) => value,
      },
      {
        id: "primarycontactemail",
        title: "Email",
        key: "primarycontactemail",
        iskey: true,
        isSortable: true,
        render: (value) => value || "N/A",
      },
      {
        id: "primarycontactphoneno",
        iskey: true,
        title: "Phone Number",
        key: "primarycontactphoneno",
        isSortable: true,
        render: (value) => value,
      },
      {
        id: "isactive",
        title: "Status",
        key: "isactive",
        isSortable: true,
        render: (rowData) => {
          const { agencyid, isactive } = rowData;
          return (
            <div className="d-flex flex-column flex-md-row align-items-center justify-content-center justify-content-md-start gap-2 px-2">
              <div className="form-check form-switch d-flex align-items-center">
                <input
                  className="form-check-input"
                  type="checkbox"
                  id={`switch-${agencyid}`}
                  defaultChecked={isactive}
                  onChange={() => agencyActivate(rowData)}
                  style={{
                    cursor: "pointer",
                    width: "2.5rem",
                    height: "1.1rem",
                  }}
                />
                {/* <label
                  className="form-check-label ms-2 text-nowrap"
                  htmlFor={`switch-${agencyid}`}
                  style={{
                    fontSize: "0.85rem",
                  }}
                >
                  {isactive ? "Active" : "Inactive"}
                </label> */}
              </div>
            </div>
          );
        },
      },
      {
        id: "action",
        title: "Action",
        key: "agencyid",
        render: (value) => (
          <div className="d-flex flex-row justify-content-start align-items-center gap-3 px-2 w-100">
            {/* Edit Icon */}
            <div
              title={"Update"}
              style={{
                cursor: value.isactive ? "pointer" : "default",
                color: value.isactive ? "" : "#a4b9ce",
                fontSize: "1rem",
              }}
              onClick={
                value.isactive
                  ? () => {
                      updateSystemUserForm.setValues({
                        agencyName: value.agency || "",
                        address: value.address || "",
                        state: value.state || "",
                        city: value.city || "",
                        contactEmail: value.primarycontactemail || "",
                        contactName: value.primarycontactname || "",
                        contactPhone: value.primarycontactphoneno || "",
                        zipcode: value.zipcode || "",
                      });
                      setSelectedAgencyId(value);
                      setUpdateAgencyModal(true);
                    }
                  : null
              }
            >
              <FontAwesomeIcon icon={faEdit} />
            </div>

            {/* Upload Icon */}
            <div
              className="d-flex align-items-center"
              style={{
                cursor: value.isactive ? "pointer" : "default",
                color: value.isactive ? "" : "#a4b9ce",
                fontSize: "1rem",
              }}
              onClick={
                value.isactive
                  ? () => {
                      setSelectedAgencyId(value);
                      setFileUploadModal(true);
                    }
                  : null
              }
              title={
                value.isactive ? "Upload File" : "Inactive - Upload Disabled"
              }
            >
              <FontAwesomeIcon icon={faUpload} />
            </div>

            {/* Delete Icon */}
            <div
              className="d-flex align-items-center"
              style={{
                cursor: "pointer",
                color: "red",
                fontSize: "1rem",
              }}
              onClick={() => {
                setSelectedAgencyId(value);
                setDeleteAgency(true);
              }}
              title="Delete Agency"
            >
              <FontAwesomeIcon icon={faTrash} />
            </div>
          </div>
        ),
      },
    ];
  };

  return (
    <div className=" flex flex-row">
      <ToastContainer />

      <div className="my-3 p-4 rounded-xl shadow-sm m-0 bg-white">
        <Row className="mb-4">
          <Col md={12} className="d-flex justify-content-between">
            <div className="d-flex gap-4 align-items-center">
              <h1 className="m-0 display-4">Agency List</h1>
              <input
                type="text"
                className="d-inline-flex focus-ring focus-ring-light py-1 px-2 text-decoration-none border rounded-2"
                style={{ width: "23rem" }}
                placeholder="Search by Agency Name"
                value={searchQuery}
                onChange={handleSearch}
                onBlur={handleSearchBlur}
                maxLength={50}
              />
            </div>
            <Button
              // variant="primary"
              onClick={() => setCreateAgencyModal(true)}
              style={{ color: "white" }}
            >
              Create Agency
            </Button>
          </Col>
        </Row>
        <DynamicTable
          cols={cols(
            navigate,
            agencyActivate,
            setSelectedAgencyId,
            setUpdateAgencyModal,
            setFileUploadModal,
            setDeleteAgency,
            updateSystemUserForm
          )}
          response={currentItems}
          isPaginate={false}
          numberOfData={5}
          headerClass="table-header"
          rowHover={true}
          fixedHeight={false}
          isBgTransparent={false}
          hideHeader={false}
          isloading={isLoading}
        />
      </div>

      <Modal show={createAgencyModal} onHide={handleCloseAgencyModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add New Agency</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={createSystemUserForm.handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Agency Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Agency Name"
                    name="agencyName"
                    value={createSystemUserForm.values.agencyName}
                    onChange={createSystemUserForm.handleChange}
                    onBlur={createSystemUserForm.handleBlur}
                    isInvalid={
                      createSystemUserForm.touched.agencyName &&
                      createSystemUserForm.errors.agencyName
                    }
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createSystemUserForm.errors.agencyName}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Contact Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Contact Name"
                    name="contactName"
                    value={createSystemUserForm.values.contactName}
                    onChange={createSystemUserForm.handleChange}
                    onBlur={createSystemUserForm.handleBlur}
                    isInvalid={
                      createSystemUserForm.touched.contactName &&
                      createSystemUserForm.errors.contactName
                    }
                    maxLength={30}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createSystemUserForm.errors.contactName}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              {/* Agency Name Field */}
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Contact Email</Form.Label>
                  <Form.Control
                    type="email"
                    placeholder="Enter Contact Email"
                    name="contactEmail"
                    value={createSystemUserForm.values.contactEmail}
                    onChange={createSystemUserForm.handleChange}
                    onBlur={createSystemUserForm.handleBlur}
                    isInvalid={
                      createSystemUserForm.touched.contactEmail &&
                      createSystemUserForm.errors.contactEmail
                    }
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createSystemUserForm.errors.contactEmail}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Contact Phone</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Contact Phone"
                    name="contactPhone"
                    value={createSystemUserForm.values.contactPhone}
                    // onChange={createSystemUserForm.handleChange}
                    onChange={(e) => {
                      if (e.target.value.match(/^\d*$/)) {
                        // Only allow numbers
                        createSystemUserForm.handleChange(e);
                      }
                    }}
                    onBlur={createSystemUserForm.handleBlur}
                    isInvalid={
                      createSystemUserForm.touched.contactPhone &&
                      createSystemUserForm.errors.contactPhone
                    }
                    maxLength={20}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createSystemUserForm.errors.contactPhone}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter City"
                    name="city"
                    value={createSystemUserForm.values.city}
                    onChange={createSystemUserForm.handleChange}
                    onBlur={createSystemUserForm.handleBlur}
                    isInvalid={
                      createSystemUserForm.touched.city &&
                      createSystemUserForm.errors.city
                    }
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createSystemUserForm.errors.city}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter State"
                    name="state"
                    value={createSystemUserForm.values.state}
                    onChange={createSystemUserForm.handleChange}
                    onBlur={createSystemUserForm.handleBlur}
                    isInvalid={
                      createSystemUserForm.touched.state &&
                      createSystemUserForm.errors.state
                    }
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createSystemUserForm.errors.state}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Address"
                    name="address"
                    value={createSystemUserForm.values.address}
                    onChange={createSystemUserForm.handleChange}
                    onBlur={createSystemUserForm.handleBlur}
                    isInvalid={
                      createSystemUserForm.touched.address &&
                      createSystemUserForm.errors.address
                    }
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createSystemUserForm.errors.address}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Zipcode</Form.Label>
                  <Form.Control
                    type="text" // Change type to text
                    placeholder="Enter Zipcode"
                    name="zipcode"
                    value={createSystemUserForm.values.zipcode}
                    onChange={(e) => {
                      if (e.target.value.match(/^\d*$/)) {
                        // Only allow numbers
                        createSystemUserForm.handleChange(e);
                      }
                    }}
                    onBlur={createSystemUserForm.handleBlur}
                    isInvalid={
                      createSystemUserForm.touched.zipcode &&
                      createSystemUserForm.errors.zipcode
                    }
                    maxLength={10}
                  />
                  <Form.Control.Feedback type="invalid">
                    {createSystemUserForm.errors.zipcode}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>
            <Modal.Footer>
              <Button
                type="submit"
                // variant="primary"
                style={{ color: "white" }}
              >
                Submit
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={updateAgencyModal}
        onHide={handleUpdateCloseAgencyModal}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Update Agency</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={updateSystemUserForm.handleSubmit}>
            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Agency Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Agency Name"
                    name="agencyName"
                    value={updateSystemUserForm.values.agencyName}
                    onChange={updateSystemUserForm.handleChange}
                    onBlur={updateSystemUserForm.handleBlur}
                    isInvalid={
                      updateSystemUserForm.touched.agencyName &&
                      updateSystemUserForm.errors.agencyName
                    }
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    {updateSystemUserForm.errors.agencyName}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Contact Name</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Contact Name"
                    name="contactName"
                    value={updateSystemUserForm.values.contactName}
                    onChange={updateSystemUserForm.handleChange}
                    onBlur={updateSystemUserForm.handleBlur}
                    isInvalid={
                      updateSystemUserForm.touched.contactName &&
                      updateSystemUserForm.errors.contactName
                    }
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    {updateSystemUserForm.errors.contactName}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              {/* Agency Name Field */}
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Contact Email</Form.Label>
                  <Form.Control
                    type="Email"
                    placeholder="Enter Contact Email"
                    name="contactEmail"
                    value={updateSystemUserForm.values.contactEmail}
                    onChange={updateSystemUserForm.handleChange}
                    onBlur={updateSystemUserForm.handleBlur}
                    isInvalid={
                      updateSystemUserForm.touched.contactEmail &&
                      updateSystemUserForm.errors.contactEmail
                    }
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    {updateSystemUserForm.errors.contactEmail}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Contact Phone</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter Contact Phone"
                    name="contactPhone"
                    value={updateSystemUserForm.values.contactPhone}
                    onChange={updateSystemUserForm.handleChange}
                    onBlur={updateSystemUserForm.handleBlur}
                    isInvalid={
                      updateSystemUserForm.touched.contactPhone &&
                      updateSystemUserForm.errors.contactPhone
                    }
                    maxLength={15}
                  />
                  <Form.Control.Feedback type="invalid">
                    {updateSystemUserForm.errors.contactPhone}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>City</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter City"
                    name="city"
                    value={updateSystemUserForm.values.city}
                    onChange={updateSystemUserForm.handleChange}
                    onBlur={updateSystemUserForm.handleBlur}
                    isInvalid={
                      updateSystemUserForm.touched.city &&
                      updateSystemUserForm.errors.city
                    }
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    {updateSystemUserForm.errors.city}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter State"
                    name="state"
                    value={updateSystemUserForm.values.state}
                    onChange={updateSystemUserForm.handleChange}
                    onBlur={updateSystemUserForm.handleBlur}
                    isInvalid={
                      updateSystemUserForm.touched.state &&
                      updateSystemUserForm.errors.state
                    }
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    {updateSystemUserForm.errors.state}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>

            <div className="row">
              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Address</Form.Label>
                  <Form.Control
                    type="text"
                    placeholder="Enter Address"
                    name="address"
                    value={updateSystemUserForm.values.address}
                    onChange={updateSystemUserForm.handleChange}
                    onBlur={updateSystemUserForm.handleBlur}
                    isInvalid={
                      updateSystemUserForm.touched.address &&
                      updateSystemUserForm.errors.address
                    }
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    {updateSystemUserForm.errors.address}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>

              <div className="col-md-6">
                <Form.Group className="mb-3">
                  <Form.Label>Zipcode</Form.Label>
                  <Form.Control
                    type="tel"
                    placeholder="Enter Zipcode"
                    name="zipcode"
                    value={updateSystemUserForm.values.zipcode}
                    onChange={(e) => {
                      if (e.target.value >= 0 || e.target.value === "") {
                        updateSystemUserForm.handleChange(e);
                      }
                    }}
                    onBlur={updateSystemUserForm.handleBlur}
                    isInvalid={
                      updateSystemUserForm.touched.zipcode &&
                      updateSystemUserForm.errors.zipcode
                    }
                    maxLength={50}
                  />
                  <Form.Control.Feedback type="invalid">
                    {updateSystemUserForm.errors.zipcode}
                  </Form.Control.Feedback>
                </Form.Group>
              </div>
            </div>
            <Modal.Footer>
              <Button
                type="submit"
                // variant="primary"
                style={{ color: "white" }}
              >
                Update
              </Button>
            </Modal.Footer>
          </Form>
        </Modal.Body>
      </Modal>

      <Modal
        show={fileUploadModal}
        onHide={() => setFileUploadModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>
            Upload Tags -{" "}
            <span style={{ fontWeight: "lighter", fontSize: "20px" }}>
              {selectedAgencyId?.agency}
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <div className="d-flex flex-column align-items-center justify-content-center">
            <Form.Group controlId="fileUpload" className="w-100">
              {!uploadedFile ? (
                <>
                  <Form.Control
                    type="file"
                    accept=".xlsx"
                    style={{ display: "none" }}
                    id="fileUpload"
                    onChange={handleFileUpload}
                  />
                  <Form.Label
                    className="btn btn-primary w-100 text-center text-gray bg-tertiary border-0"
                    htmlFor="fileUpload"
                  >
                    <FontAwesomeIcon icon={faUpload} className="text-gray" />{" "}
                    Select File
                  </Form.Label>
                </>
              ) : (
                <div className="d-flex flex-column align-items-center w-100">
                  <div className="text-truncate mb-2 w-100 text-center">
                    <strong>Selected File:</strong> {uploadedFile.name}
                  </div>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={handleFileRemove}
                    className="w-50"
                  >
                    <FontAwesomeIcon icon={faTrash} /> Remove
                  </Button>
                </div>
              )}
            </Form.Group>
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button
            type="submit"
            variant="primary"
            className="text-white"
            disabled={isButtonClicked || !uploadedFile}
            onClick={handleSubmit}
          >
            {isButtonClicked ? (
              <Spinner animation="border" size="sm" />
            ) : (
              "Upload"
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={deleteAgency} onHide={() => setDeleteAgency(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Delete Agency</Modal.Title>
        </Modal.Header>
        <Modal.Body className="my-3 display-6">
          Are you sure want to delete the{" "}
          <span style={{ fontWeight: "bold", fontSize: "18px" }}>
            {selectedAgencyId?.agency}
          </span>{" "}
          Agency?
        </Modal.Body>
        <Modal.Footer>
          <Button variant="light" onClick={() => setDeleteAgency(false)}>
            Cancel
          </Button>
          <Button
            // variant="primary"
            style={{ backgroundColor: "red", border: "none", color: "white" }}
            onClick={handleDeleteConfirm}
          >
            Delete
          </Button>
        </Modal.Footer>
      </Modal>
      <ReactPaginate
        previousLabel={"<"}
        nextLabel={">"}
        breakLabel={"..."}
        pageCount={pageCount}
        marginPagesDisplayed={1}
        pageRangeDisplayed={1}
        onPageChange={handlePageClick}
        containerClassName={"pagination justify-content-end mt-4"}
        pageClassName={"page-item"}
        pageLinkClassName={"page-link"}
        previousClassName={"page-item"}
        previousLinkClassName={"page-link"}
        nextClassName={"page-item"}
        nextLinkClassName={"page-link"}
        activeClassName={"active"}
        forcePage={currentPage}
      />
    </div>
  );
};

export default Users;
